
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft } from "lucide-react";

interface ForgotPasswordFormProps {
  onBackToSignIn: () => void;
}

export default function ForgotPasswordForm({ onBackToSignIn }: ForgotPasswordFormProps) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [debugLink, setDebugLink] = useState<string | null>(null);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('send-recovery-email', {
        body: { email },
      });

      if (error) throw error;

      // Se vier um link de debug (sandbox), use-o mesmo em caso de falha de envio
      if (data?.debugLink && data?.success === false) {
        setDebugLink(data.debugLink);
        setEmailSent(true);
        toast({
          title: "Link gerado",
          description: "Use o botão para redefinir sua senha agora mesmo.",
        });
        return;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Não foi possível enviar o e-mail de recuperação.');
      }

      if (data?.debugLink) setDebugLink(data.debugLink);

      setEmailSent(true);
      toast({
        title: "E-mail enviado!",
        description: "Verifique sua caixa de entrada (e o spam) para redefinir sua senha.",
      });
    } catch (error: any) {
      toast({
        title: "Erro ao enviar e-mail",
        description: error?.message || 'Não foi possível enviar o e-mail de recuperação.',
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="text-center">
        <div className="mb-8">
          <div className="text-6xl mb-4">📧</div>
          <h2 className="text-3xl font-bold mb-2 text-krecao-yellow">E-mail Enviado!</h2>
          <p className="text-gray-400 mb-4">
            Enviamos um link para redefinir sua senha para:
          </p>
          <p className="text-krecao-yellow font-semibold text-lg mb-6">{email}</p>
          
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">🌭</span>
              <span className="text-krecao-yellow font-bold">K-recão Lanches</span>
            </div>
            <p className="text-sm text-gray-300 text-left">
              Verifique seu e-mail e clique no botão "Redefinir Minha Senha" para criar uma nova senha e voltar a fazer seus pedidos favoritos!
            </p>
          </div>
        </div>
        
        <div className="space-y-4">
          <p className="text-sm text-gray-400">
            Não recebeu o e-mail? Verifique sua pasta de spam ou tente novamente.
          </p>

          {debugLink && (
            <a
              href={debugLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-full items-center justify-center rounded-md border border-gray-600 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
            >
              Abrir link de redefinição agora
            </a>
          )}
          
          <Button
            type="button"
            variant="outline"
            onClick={() => setEmailSent(false)}
            className="w-full border-gray-600 text-white hover:bg-gray-800"
          >
            Tentar outro e-mail
          </Button>
          
          <Button
            type="button"
            variant="ghost"
            onClick={onBackToSignIn}
            className="w-full text-krecao-yellow hover:text-krecao-yellow/80 hover:bg-gray-800"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar ao login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mb-8 text-center">
        <div className="text-6xl mb-4">🔐</div>
        <h2 className="text-3xl font-bold mb-2">Recuperar Senha</h2>
        <p className="text-gray-400">
          Digite seu e-mail para receber um link de recuperação
        </p>
      </div>

      <form onSubmit={handleResetPassword}>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">E-mail</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="exemplo@email.com"
              required
              className="bg-gray-800 border-gray-700 text-white"
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-krecao-red hover:bg-krecao-red/90"
            disabled={loading}
          >
            {loading ? "Enviando..." : "Enviar Link de Recuperação"}
          </Button>
        </div>
      </form>

      <div className="mt-6 text-center">
        <button
          type="button"
          onClick={onBackToSignIn}
          className="text-krecao-yellow hover:underline text-sm flex items-center justify-center gap-2 mx-auto"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar ao login
        </button>
      </div>
    </>
  );
}
